import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  FormGroup,
  Input,
  Card,
  CardBody,
  UncontrolledTooltip,
} from "reactstrap";

import { TRAINER_STATUS } from "../../Api/constants";
import { useAppDispatch, useAppSelector } from "../../ReduxToolkit/Hooks";
import { getTrainerAndRoomAvailability } from "../../ReduxToolkit/Reducers/LoadFactorSlice";
import {
  fetchAvailabilityCalendar,
  getClassRoomList,
  getLocationList,
} from "../../ReduxToolkit/Reducers/MasterSlice";
import { getUsersList } from "../../ReduxToolkit/Reducers/UserSlice";
import {
  AdditionalInformation,
  Available,
  CourseName,
  Days,
  From,
  HappeningAt,
  Holidays,
  Location,
  MonthlyTotals,
  monthNamesLong,
  Room,
  SelectMonth,
  SelectYear,
  Sunday,
  To,
  Trainer,
  Utilized,
} from "../../utils/Constant";
import {
  getFormattedDays,
  getOrdinalSuffix,
  getRandomInteger,
} from "../../utils/helper/helper";
import { CheckAvailabilityProps } from "../../utils/helper/propTypes";
import DetailHeader from "../DetailHeader";
import DetailHeaderContainer from "../DetailHeaderContainer";
import LabelTooltip from "../LabelTooltip";
import CommonModal from "../Modal";

import RoundedCard from "./RoundedCard";
import "./styles.scss";

const CalendarAvailability: React.FC<CheckAvailabilityProps> = ({
  locationId,
  checkingFor,
}) => {
  const dispatch = useAppDispatch();
  const [calendarData, setCalendarData] = useState<any>(null);
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );
  const [selectedMonth, setSelectedMonth] = useState<string>(
    new Date().toLocaleString("en-US", { month: "long" })
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clickedBooking, setClickedBooking] = useState<any>(null);
  const [bookings, setBookings] = useState<any[]>([]);
  const [days, setDays] = useState<string[]>([]);
  const years = Array.from(
    { length: 10 },
    (_, i) => new Date().getFullYear() + i
  );
  const [classRoomNameMap, setClassRoomNameMap] = useState<any>({});
  const [userMap, setUserMap] = useState<any>({});
  const { usersList } = useAppSelector(state => state.user);
  const { availabilityData } = useAppSelector(state => state.loadFactor);
  const passType =
    checkingFor === "classroom"
      ? "class-room-availability"
      : "trainer-availability";
  const { calendarAvailabilityData, classRoomList } = useAppSelector(
    state => state.master
  );
  const [holidayDays, setHolidayDays] = useState<Set<number>>(new Set());
  const { locationList } = useAppSelector(state => state.master);
  const [locationMap, setLocationMap] = useState<any>({});

  useEffect(() => {
    dispatch(getLocationList());
  }, [dispatch]);

  useEffect(() => {
    const locationMap = {};
    locationList?.location?.forEach((location: any) => {
      locationMap[location.id] = location.name;
    });
    setLocationMap(locationMap);
  }, [locationList]);

  useEffect(() => {
    if (
      calendarAvailabilityData?.calendar &&
      Array.isArray(calendarAvailabilityData?.calendar)
    ) {
      const holidays = new Set<number>();
      calendarAvailabilityData?.calendar.forEach((item: any) => {
        if (item.date) {
          const dateObj = new Date(item.date);
          const month = dateObj.toLocaleString("en-US", { month: "long" });
          const year = dateObj.getFullYear();
          if (month === selectedMonth && year === selectedYear) {
            holidays.add(dateObj.getDate());
          }
        }
      });
      setHolidayDays(holidays);
    } else {
      setHolidayDays(new Set());
    }
  }, [calendarAvailabilityData, selectedMonth, selectedYear]);

  useEffect(() => {
    dispatch(getUsersList({ role: TRAINER_STATUS }));
    dispatch(
      getClassRoomList({
        id: locationId,
      })
    );
  }, [dispatch, locationId]);

  useEffect(() => {
    dispatch(
      getTrainerAndRoomAvailability({
        type: passType,
        location_id: checkingFor === "classroom" ? locationId : null,
        year: selectedYear,
      })
    );
  }, [locationId, passType, dispatch, selectedYear, checkingFor]);

  useEffect(() => {
    if (locationId) {
      dispatch(
        fetchAvailabilityCalendar({
          date: null,
          location_id: locationId,
          year: selectedYear?.toString(),
        })
      );
    }
  }, [dispatch, locationId, selectedYear]);

  useEffect(() => {
    const userMapObj: any = {};
    usersList?.users?.forEach((user: any) => {
      userMapObj[user.id] = user;
    });
    setUserMap(userMapObj);
  }, [usersList]);

  useEffect(() => {
    const classRoomMapObj: any = {};
    classRoomList?.class_room?.forEach((class_room: any) => {
      classRoomMapObj[class_room?.id?.toString()] = class_room?.name;
    });
    setClassRoomNameMap(classRoomMapObj);
  }, [classRoomList]);

  useEffect(() => {
    let rows: string[] = [];
    const monthBookings: Record<string, any[]> = {};
    const monthKey = selectedMonth.toLowerCase();

    if (checkingFor === "classroom" && classRoomList?.class_room) {
      rows = classRoomList?.class_room?.map((room: any) => room.id?.toString());
      monthBookings[`${selectedYear}-${selectedMonth}`] = [];
      rows.forEach(classroomId => {
        const bookingsArr =
          availabilityData?.classroom?.[classroomId]?.[monthKey] || [];
        bookingsArr.forEach((item: any) => {
          monthBookings[`${selectedYear}-${selectedMonth}`].push({
            rowItem: classroomId,
            rowLabel: item.course_name,
            start: parseInt(item.start_date.split("-")[0]),
            end: parseInt(item.end_date.split("-")[0]),
            month: selectedMonth,
            ...item,
          });
        });
      });
    } else if (checkingFor === "trainer" && usersList?.users) {
      rows = usersList.users.map((user: any) => user.id?.toString());
      monthBookings[`${selectedYear}-${selectedMonth}`] = [];
      rows.forEach(trainerId => {
        const bookingsArr =
          availabilityData?.trainer?.[trainerId]?.[monthKey] || [];
        bookingsArr.forEach((item: any) => {
          monthBookings[`${selectedYear}-${selectedMonth}`].push({
            rowItem: trainerId,
            rowLabel: item.course_name,
            start: parseInt(item.start_date.split("-")[0]),
            end: parseInt(item.end_date.split("-")[0]),
            month: selectedMonth,
            ...item,
          });
        });
      });
    }
    setCalendarData({ rows, monthBookings });
    setBookings(monthBookings[`${selectedYear}-${selectedMonth}`] || []);
    setDays(getFormattedDays(selectedYear, selectedMonth, monthNamesLong));
  }, [
    availabilityData,
    selectedYear,
    selectedMonth,
    checkingFor,
    classRoomList,
    usersList,
    locationId,
  ]);

  const handleCardClick = (booking: any) => {
    setClickedBooking(booking);
    toggleModal();
  };
  const toggleModal = () => {
    setIsModalOpen(prev => !prev);
  };

  const getModalTitle = () => {
    return AdditionalInformation;
  };

  const renderModalBody = () => {
    return (
      <>
        <Card className="p-2 border border-grey">
          <CardBody>
            <div className="mt-1">
              <Row>
                <Col>
                  <DetailHeader
                    assign={CourseName}
                    name={clickedBooking?.rowLabel}
                  />
                </Col>
                <Col>
                  <DetailHeader
                    assign={checkingFor === "classroom" ? HappeningAt : Trainer}
                    name={
                      checkingFor === "classroom"
                        ? classRoomNameMap[clickedBooking?.rowItem]
                        : userMap[clickedBooking?.rowItem]?.user_name
                    }
                  />
                </Col>
              </Row>
              <Row className="mt-4">
                <Col>
                  <DetailHeader
                    assign={From}
                    name={`${clickedBooking?.start} ${getOrdinalSuffix(clickedBooking?.start)} ${clickedBooking?.month}`}
                  />
                </Col>
                <Col>
                  <DetailHeader
                    assign={To}
                    name={`${clickedBooking?.end} ${getOrdinalSuffix(clickedBooking?.end)} ${clickedBooking?.month}`}
                  />
                </Col>
              </Row>
            </div>
          </CardBody>
        </Card>
      </>
    );
  };

  return (
    <div className="learning-assessment overflow-x-auto px-4">
      <CommonModal
        sizeTitle={getModalTitle()}
        modalBodyClassName=""
        isOpen={isModalOpen}
        toggle={toggleModal}
        backdrop="static"
        size="lg"
        showFooter={false}
      >
        {renderModalBody()}
      </CommonModal>
      <div className="mb-4 flex gap-4 align-items-center calendar-legend-row">
        <Row>
          <Col lg="3">
            <FormGroup>
              <LabelTooltip
                label={SelectYear}
                tooltipText={SelectYear}
                important={true}
              />
              <Input
                name="select_year"
                type="select"
                value={selectedYear}
                onChange={(e: { target: { value: any } }) =>
                  setSelectedYear(Number(e.target.value))
                }
              >
                {years?.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col lg="3">
            <FormGroup>
              <LabelTooltip
                label={SelectMonth}
                tooltipText={SelectMonth}
                important={true}
              />
              <Input
                name="select_month"
                type="select"
                value={selectedMonth}
                onChange={(e: { target: { value: any } }) =>
                  setSelectedMonth(e.target.value)
                }
              >
                {monthNamesLong?.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col lg="3" className="mt-2 ms-2">
            <DetailHeaderContainer
              assign={Location}
              name={locationMap[locationId] || "-"}
              key={getRandomInteger(1, 100)}
            />
          </Col>
        </Row>
        <div className="calendar-legends d-flex align-items-center gap-3">
          <span className="legend-item">
            <span className="legend-color holiday-bg"></span>
            {Holidays}
          </span>
          <span className="legend-item">
            <span className="legend-color sunday-bg"></span>
            {Sunday}
          </span>
        </div>
      </div>
      <div className="calendar-table">
        <table className="border-collapse border">
          <thead>
            <tr>
              <th className="custom-table-cell">
                {checkingFor === "classroom" ? Room : Trainer}
              </th>
              {days?.map((day, idx) => {
                const dayNumber = idx + 1;
                const isSunday = day.includes("Sun");
                const isHoliday = holidayDays.has(dayNumber);
                let tooltipText = "";
                const tooltipId = `calendar-header-tooltip-${idx}`;
                if (isSunday) {
                  tooltipText = "Sunday";
                } else if (isHoliday && calendarAvailabilityData?.calendar) {
                  const holiday: any = calendarAvailabilityData.calendar.find(
                    (item: any) => {
                      const dateObj = new Date(item.date);
                      return (
                        dateObj.getFullYear() === selectedYear &&
                        dateObj.toLocaleString("en-US", { month: "long" }) ===
                          selectedMonth &&
                        dateObj.getDate() === dayNumber
                      );
                    }
                  );
                  tooltipText = holiday?.description || "";
                }
                return (
                  <th
                    key={day}
                    id={tooltipText ? tooltipId : undefined}
                    className={`custom-table-cell ${isSunday ? "sunday-bg" : ""} ${isHoliday ? "holiday-bg" : ""}`}
                  >
                    {day}
                    {tooltipText && (
                      <UncontrolledTooltip placement="top" target={tooltipId}>
                        {tooltipText}
                      </UncontrolledTooltip>
                    )}
                  </th>
                );
              })}
              <th className="custom-table-cell">{MonthlyTotals}</th>
            </tr>
          </thead>
          <tbody>
            {calendarData?.rows?.map(rowItem => {
              const roomBookings = bookings.filter(b => b.rowItem === rowItem);
              const utilizedDays = new Set<number>();
              roomBookings.forEach(booking => {
                for (let d = booking.start; d <= booking.end; d++) {
                  utilizedDays.add(d);
                }
              });
              let displayName = rowItem;
              if (checkingFor === "classroom") {
                displayName = classRoomNameMap[rowItem] || "";
              } else if (checkingFor === "trainer") {
                displayName = userMap[rowItem]?.user_name || "";
              }
              if (displayName)
                return (
                  <tr key={rowItem}>
                    <td className="custom-table-cell">{displayName}</td>
                    {days?.map((_, index) => {
                      const dayNumber = index + 1;
                      const overlappingBookings = roomBookings.filter(
                        b => dayNumber >= b.start && dayNumber <= b.end
                      );
                      return (
                        <td
                          key={dayNumber}
                          className={`custom-table-cell ${days[index].includes("Sun") ? "sunday-bg" : ""} ${holidayDays.has(dayNumber) ? "holiday-bg" : ""}`}
                        >
                          {overlappingBookings.length > 0 && (
                            <div className="relative h-full flex flex-col space-y-1">
                              {overlappingBookings.map((booking, i) => (
                                <RoundedCard
                                  key={i}
                                  size="small"
                                  onClick={() => {
                                    handleCardClick(booking);
                                  }}
                                >
                                  {booking.rowLabel}
                                </RoundedCard>
                              ))}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className="custom-table-cell">
                      {`${Available} ${Days} : `} {days.length}
                      <br />
                      {`${Utilized} ${Days} : `} {utilizedDays.size} (
                      {((utilizedDays.size / days.length) * 100).toFixed(1)}%)
                    </td>
                  </tr>
                );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CalendarAvailability;
